import FlightDetailsFooter from '@/src/components/desktop/srp/flightResults/flightDetails/flightDetailsFooter'
import FlightDetailsOffersWrapper from '@/src/components/common/srp/flightDetailsOffers'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useFlightDetails } from '@/src/hooks/srp/flightDetails'
import { FlightCard, JsonsData, Sectors } from '@/src/types/flight'
import { getErrorMessage } from '@/src/utils/srp/searchUtils/getErrorMessage'
import { KeyboardArrowDownRounded } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import FlightDetails from './flightDetails'

interface FlightDetailsProps {
    onSelect: () => void
    flights: FlightCard[]
    sectors: Sectors
    jsons: JsonsData
    sid: string
    isRTSolutionView?: boolean
    showBaggage?: boolean
}

const FlightDetailsWrapper: FC<FlightDetailsProps> = ({
    flights,
    sectors,
    jsons,
    onSelect,
    sid,
    isRTSolutionView,
    showBaggage
}) => {
    const {
        baggageData,
        baggageLoading,
        baggageRTKError,
        miniRuleLoading,
        miniRuleRTKError,
        minirule = [],
        prices,
        footerButtonCTA,
        retryCount,
        handleRetryBaggage,
        maxRetries,
        applicableCouponsData
    } = useFlightDetails({
        flights,
        jsons,
        sectors,
        onSelect,
        sid,
        showBaggage,
        isRTSolutionView
    })
    const { formatDate } = useFormatDate()
    const tFlightDetails = useTranslations('srpFlightDetails')
    const tCommon = useTranslations('common')
    const [hasOffers, setHasOffers] = useState(false)
    const [isOffersInView, setIsOffersInView] = useState(false)
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const offersContainerRef = useRef<HTMLDivElement | null>(null)

    const baggageError = getErrorMessage(baggageRTKError)
    const miniRuleError = getErrorMessage(miniRuleRTKError)

    useEffect(() => {
        if (!hasOffers) {
            setIsOffersInView(false)
            return
        }
        if (!scrollContainerRef.current || !offersContainerRef.current) return

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    setIsOffersInView(entry.isIntersecting)
                })
            },
            {
                root: scrollContainerRef.current,
                rootMargin: '50px',
                threshold: 0.1
            }
        )

        observer.observe(offersContainerRef.current)

        return () => {
            observer.disconnect()
        }
    }, [hasOffers])

    const showOffersNudge = hasOffers && !isOffersInView

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '90vh',
                maxHeight: '90vh',
                overflow: 'hidden'
            }}
        >
            {/* Header - Fixed at top */}
            <Box
                sx={{
                    p: 2,
                    pb: 1,
                    backgroundColor: 'white',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Typography sx={{ fontSize: '17px', fontWeight: 600 }}>{tFlightDetails('flightDetails')}</Typography>
            </Box>

            {/* Body - Scrollable */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 2,
                    py: 2,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                {flights.map((flight, index) => {
                    const targetSectors = useMemo(
                        () => flight?.sectorKeys?.map(sectorKey => sectors[sectorKey]),
                        [flight, sectors]
                    )
                    return (
                        <Box
                            key={index}
                            sx={{
                                mb: 2,
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none'
                            }}
                        >
                            <ErrorBoundaryWrapper identifier={`flight-details-${index}-PWA`}>
                                <FlightDetails
                                    baggageError={baggageError}
                                    baggageLoading={baggageLoading}
                                    baggageData={baggageData}
                                    jsons={jsons}
                                    targetSectors={targetSectors}
                                    minirule={minirule[index] || {}}
                                    miniRuleLoading={miniRuleLoading}
                                    miniRuleError={miniRuleError}
                                    tFlightDetails={tFlightDetails}
                                    tCommon={tCommon}
                                    formatDate={formatDate}
                                    showBaggage={showBaggage}
                                    retryCount={retryCount}
                                    handleRetryBaggage={handleRetryBaggage}
                                    maxRetries={maxRetries}
                                />
                            </ErrorBoundaryWrapper>
                        </Box>
                    )
                })}
                <FlightDetailsOffersWrapper
                    tags={applicableCouponsData?.tags?.tags || []}
                    offersContainerRef={offersContainerRef}
                    onOffersCountChange={count => setHasOffers(count > 0)}
                />
            </Box>
            {showOffersNudge && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 90,
                        transform: 'translateX(-50%)',
                        zIndex: 11,
                        bgcolor: '#ffc621',
                        color: '#1a1a1a',
                        borderRadius: '999px',
                        height: '42px',
                        px: 3,
                        py: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        boxShadow: '0px 4px 10px rgba(0,0,0,0.18)',
                        pointerEvents: 'none'
                    }}
                >
                    <Box component="span" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>
                        {tFlightDetails('specialOffers')}
                    </Box>
                    <KeyboardArrowDownRounded sx={{ fontSize: 30 }} />
                </Box>
            )}

            {/* Footer - Fixed at bottom */}
            <Box
                sx={{
                    backgroundColor: 'white',
                    flexShrink: 0,
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10
                }}
            >
                <ErrorBoundaryWrapper identifier="flight-details-footer-PWA">
                    <FlightDetailsFooter
                        showSelectButton={true}
                        price={prices.price}
                        strikeOffPrice={prices.strikeOffPrice}
                        onSelect={onSelect}
                        buttonCTA={footerButtonCTA}
                    />
                </ErrorBoundaryWrapper>
            </Box>
        </Box>
    )
}

export default FlightDetailsWrapper
